import { Box } from '@mui/joy';
import {
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetDataPath,
  GetMainMenuItemsParams,
  ICellRendererParams,
  IRowNode,
  MenuItemDef,
  RowHeightParams,
} from 'ag-grid-community';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IDefaultVendor } from 'contacts/types';
import { AgGridTable, SlidingPanelContent } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { isEmptyArray, isPromotedSingleChild } from 'core/utils';
import { useGetShipTime } from 'inventory/hooks';
import { IShipTime } from 'inventory/types';
import { useGetPartInventory } from 'parts/hooks';
import { IPart, IPartInventoryItem, IPartInventoryPayload } from 'parts/types';
import { sumQuantityAvailable } from 'parts/utils';
import { useCostDecimals } from 'pricing/hooks';

import { LocationCell } from './cells';
import { columnDef, vendorInventoryColumns } from './columns';
import { GroupStripeRefresher } from './GroupStripeRefresher';
import { InventoryCards } from './InventoryCards';
import { LocationsCountStatusBar } from './LocationsCountStatusBar';
import { TotalsStatusBar } from './TotalsStatusBar';
import { getLocationKey, getLocationValue } from './utils';
import { VendorTotalsStatusBar } from './VendorTotalsStatusBar';
import './style.css';
import { PartDetailsTab } from '../enums';
import {
  hiddenNoRowsOverlaySx,
  useLoadErrorNotification,
  usePartDetailsScrollToFocus,
} from '../hooks';

interface IProps {
  part?: IPart | null;
  enabled?: boolean;
  isAlternateVersionEnabled?: boolean;
  fullWidth?: boolean;
}

// Auto-focus only kicks in past 3 rows; below that the inline space fits comfortably.
const MIN_ROWS_FOR_FOCUS_TRIGGER = 3;

export const Inventory = ({
  part,
  enabled,
  isAlternateVersionEnabled,
  fullWidth = true,
}: IProps) => {
  const { t } = useTranslation();

  const costDecimals = useCostDecimals();

  const [shipTypeCheckEnabled, setShipTypeCheckEnabled] = useState(false);
  const [shipTypeError, setShipTypeError] = useState(null);
  const [shipTimes, setShipTimes] = useState<IShipTime['shipTimes']>({});
  const [shipTimesFetched, setShipTimesFetched] = useState(false);
  const [inventoryRecords, setInventoryRecords] = useState<IPartInventoryItem[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<IDefaultVendor | null>(null);
  const [firstCardTotal, setFirstCardTotal] = useState<number | null>(null);
  const [firstCardUom, setFirstCardUom] = useState<string | undefined>(undefined);
  const [computedVendorTotals, setComputedVendorTotals] = useState<Record<number, number>>({});

  const { panelScrollAnchorRef, onBodyScroll } = usePartDetailsScrollToFocus(
    PartDetailsTab.INVENTORY,
    {
      getRowCount: () => inventoryRecords.length,
      minRowsForTrigger: MIN_ROWS_FOR_FOCUS_TRIGGER,
    }
  );

  const selectedVendorId = useMemo(() => selectedVendor?.id ?? null, [selectedVendor]);
  const isVendorSelected = useMemo(() => selectedVendorId !== null, [selectedVendorId]);

  // Memoize so unrelated parent re-renders (e.g. focus-mode toggle) don't produce a fresh
  // payload reference and re-trigger React Query's paginated fetch.
  const payload = useMemo<IPartInventoryPayload>(
    () => ({
      filters: {
        part: [part?.number],
        manufacturerId: part?.manufacturerId || -1,
      },
      alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
      ...(selectedVendorId !== null && { viewBy: { vendorContact: selectedVendorId } }),
    }),
    [part?.number, part?.manufacturerId, isAlternateVersionEnabled, selectedVendorId]
  );

  const {
    records,
    isLoading: isDataLoading,
    isFetching,
    isError,
    loadingPercentage,
    currentPage,
  } = useGetPartInventory(payload, { enabled, fetchAllPages: true });

  // A failed first page leaves currentPage undefined, which keeps the grid's loading overlay on.
  const isLoadFailed = (enabled ?? true) && isError && !isFetching;

  useLoadErrorNotification({
    isLoadFailed,
    title: t('errorTitleInventoryLoad'),
    message: t('messageInventoryLoadError'),
  });

  useGetShipTime(
    {
      part: part?.number,
      manufacturer: part?.manufacturerId,
    },
    shipTypeCheckEnabled,
    (response: IShipTime) => onShipTimeFetched(response),
    (response: IShipTime) => onShipTimeError(response)
  );

  const lotDetailsAvailable = useMemo(
    () =>
      inventoryRecords.some(
        (record) => record.reelLength || record.lotNumber || record.lotManufacturerName
      ),
    [inventoryRecords]
  );

  const displayRecords = useMemo<IPartInventoryItem[]>(
    () =>
      inventoryRecords.map((record) => ({
        ...record,
        shipDays: record.shipDays ?? shipTimes?.[record.locationId]?.businessTransitDays,
      })),
    [inventoryRecords, shipTimes]
  );

  const onShipTimeFetched = (response: IShipTime) => {
    setShipTypeCheckEnabled(false);
    setShipTimes(response.shipTimes ?? {});
    setShipTimesFetched(true);
  };

  const onShipTimeError = (response: IShipTime) => {
    setShipTypeCheckEnabled(false);

    setShipTypeError(response.error);
  };

  const onSelectVendor = useCallback(
    (vendor: IDefaultVendor | null) => {
      if ((vendor?.id ?? null) === selectedVendorId) {
        return;
      }
      setSelectedVendor(vendor);
      setInventoryRecords([]);
    },
    [selectedVendorId]
  );

  useEffect(() => {
    setSelectedVendor(null);
    setFirstCardTotal(null);
    setFirstCardUom(undefined);
    setInventoryRecords([]);
    setComputedVendorTotals({});
    setShipTimes({});
    setShipTimesFetched(false);
    setShipTypeCheckEnabled(false);
    setShipTypeError(null);
  }, [part?.number, part?.manufacturerId]);

  useEffect(() => {
    if (shipTimesFetched) {
      return;
    }

    const missingShipDays = isEmptyArray(records?.filter((record) => !!record?.shipDays));

    if (missingShipDays) {
      setShipTypeCheckEnabled(true);
    }
  }, [records, shipTimesFetched]);

  useEffect(() => {
    if (records?.length) {
      setInventoryRecords(records);
    } else if (!isDataLoading) {
      setInventoryRecords([]);
    }
  }, [records, isDataLoading]);

  useEffect(() => {
    if (!records?.length || isDataLoading) {
      return;
    }

    // Company view returns one row per (location, reel); vendor view returns one row per
    // location. Dedupe by location identity in both cases — for vendor data each row is
    // already unique so the dedupe is a no-op, but for company data it collapses reel
    // duplicates.
    const uniqueRecords = uniqBy(records, getLocationKey);
    const total = sumQuantityAvailable(uniqueRecords);

    if (selectedVendorId === null) {
      setFirstCardTotal(total);
      setFirstCardUom(records[0]?.uom);

      return;
    }
    setComputedVendorTotals((prev) => ({ ...prev, [selectedVendorId]: total }));
  }, [records, isDataLoading, selectedVendorId]);

  const columnDefs = useMemo(
    () =>
      isVendorSelected
        ? vendorInventoryColumns({
            isAlternateVersionEnabled,
            alternateInventoryEnabled: selectedVendor?.alternateInventoryEnabled,
          })
        : columnDef({
            shipTypeCheckEnabled,
            shipTypeError,
            isAlternateVersionEnabled,
            lotDetailsAvailable,
            costDecimals,
          }),
    [
      isVendorSelected,
      shipTypeCheckEnabled,
      shipTypeError,
      isAlternateVersionEnabled,
      lotDetailsAvailable,
      selectedVendor?.alternateInventoryEnabled,
      costDecimals,
    ]
  );

  const statusBar = useMemo(() => {
    const leftStatusPanel = isVendorSelected
      ? {
          statusPanel: VendorTotalsStatusBar,
          statusPanelParams: {},
          align: 'left' as const,
        }
      : {
          statusPanel: TotalsStatusBar,
          statusPanelParams: { isAlternateVersionEnabled },
          align: 'left' as const,
        };

    const rightStatusPanel = {
      statusPanel: LocationsCountStatusBar,
      statusPanelParams: {},
      align: 'right' as const,
    };

    return {
      statusPanels: [leftStatusPanel, rightStatusPanel],
    };
  }, [isVendorSelected, isAlternateVersionEnabled]);

  const firstCardLabel = isAlternateVersionEnabled ? t('labelTotalInventory') : t('labelCompany');

  // In vendor mode, use the staging row id format (`rowId-externalLocationId`) so the
  // flat vendor inventory list stays unique across locations. In the company tree-data
  // mode, fall back to the rowId/id pair — the tree uses getDataPath for grouping.
  const getRowId = useCallback(
    (data: AgGridDataRow) =>
      isVendorSelected
        ? `${data?.rowId ?? data?.id}-${data?.externalLocationId ?? ''}`
        : data?.rowId?.toString() || data?.id.toString(),
    [isVendorSelected]
  );

  const stripGroupingMenuItems = useCallback(
    (items: (DefaultMenuItem | MenuItemDef)[] = []) =>
      items.filter(
        (item) => typeof item !== 'string' || !['rowGroup', 'rowUnGroup'].includes(item)
      ),
    []
  );

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => stripGroupingMenuItems(params.defaultItems),
    [stripGroupingMenuItems]
  );

  const getMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => stripGroupingMenuItems(params.defaultItems),
    [stripGroupingMenuItems]
  );

  // Tree-data grouping (sticky location header, zebra stripes) only applies in the
  // company-wide view. The vendor-selected view renders a flat list of vendor records.
  const getDataPath = useCallback<GetDataPath>((data) => {
    if (!data) {
      return [];
    }
    const locationKey = getLocationKey(data);

    return [locationKey, String(data.rowId ?? data.id)];
  }, []);

  // Zebra-stripe per location group. We resolve the group's position from AG Grid's
  // post-sort sibling list so the alternation tracks the displayed order (not the
  // source-data order), staying correct when the user sorts on any column.
  const getRowClass = useCallback((params: { node?: IRowNode }) => {
    const node = params.node;

    if (!node) {
      return undefined;
    }

    const groupNode = node.group ? node : node.parent;

    if (!groupNode || !groupNode.parent) {
      return undefined;
    }

    const siblings =
      groupNode.parent.childrenAfterSort ??
      groupNode.parent.childrenAfterFilter ??
      groupNode.parent.childrenAfterGroup;

    if (!siblings) {
      return undefined;
    }

    const index = siblings.indexOf(groupNode);

    return index >= 0 && index % 2 === 1 ? 'oddGroup' : undefined;
  }, []);

  const getRowHeight = useCallback((params: RowHeightParams) => {
    if (params.node?.group) {
      return undefined;
    }
    if (isPromotedSingleChild({ node: params.node })) {
      return undefined;
    }

    return 42;
  }, []);

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: t('headerLocation'),
      minWidth: 220,
      pinned: 'left',
      lockPinned: true,
      // No floatingFilter: AG Grid v34's HeaderFilterCellCtrl crashes on the auto-group
      // column when columnDefs re-apply (useAgGridLayout.applySavedLayout). Filter is
      // still reachable via the column hamburger menu.
      filter: 'agTextColumnFilter',
      filterValueGetter: (params) =>
        params.data ? getLocationValue(params.data, isAlternateVersionEnabled) : null,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: ICellRendererParams) => {
          const isGroupRow = !!params.node?.group;

          if (!isGroupRow && !isPromotedSingleChild(params)) {
            return null;
          }

          const data = isGroupRow ? params.node.allLeafChildren?.[0]?.data : params.node?.data;

          return (
            <LocationCell
              {...params}
              data={data}
              isAlternateVersionEnabled={isAlternateVersionEnabled}
            />
          );
        },
      },
    }),
    [t, isAlternateVersionEnabled]
  );

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: t('labelColumns'),
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'filters',
          labelDefault: t('labelFilters'),
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: '',
    }),
    [t]
  );

  return (
    <Box ref={panelScrollAnchorRef} sx={{ display: 'contents' }}>
      <SelectionCountProvider>
        <AgGridClientTableProvider
          key={selectedVendorId ?? 'company'}
          tableId="partInventoryTable"
          entityType="InventoryItem"
          columnDefs={columnDefs}
          data={isLoadFailed ? [] : displayRecords}
          isDataLoading={isDataLoading}
          loadingPercentage={isLoadFailed ? 100 : loadingPercentage}
          dataCurrentPage={isLoadFailed ? 1 : currentPage}
          hasRowSelection={false}
          hasDefaultStatusBar={false}
          layoutType={TableLayoutType.PART_INVENTORY}
          hasLayoutActions
          hasManageFilters
          hasGroupByEnabled={false}
          getRowId={getRowId}
          rowBuffer={50}
          statusBar={statusBar}
          treeData={!isVendorSelected}
          getDataPath={isVendorSelected ? undefined : getDataPath}
          groupDefaultExpanded={-1}
          groupRemoveSingleChildren={!isVendorSelected}
          autoGroupColumnDef={isVendorSelected ? undefined : autoGroupColumnDef}
          suppressAggFuncInHeader
          sideBar={sideBar}
          getContextMenuItems={getContextMenuItems}
          getMainMenuItems={getMainMenuItems}
          getRowClass={isVendorSelected ? undefined : getRowClass}
          getRowHeight={isVendorSelected ? undefined : getRowHeight}
          onBodyScroll={onBodyScroll}
          suppressRowTransform={isVendorSelected}
        >
          {!isVendorSelected && <GroupStripeRefresher />}
          <SlidingPanelContent title={t('titleInventory')} fullWidth={fullWidth}>
            <>
              <InventoryCards
                partNumber={part?.number}
                manufacturerId={part?.manufacturerId}
                enabled={enabled}
                firstCardLabel={firstCardLabel}
                firstCardTotal={firstCardTotal}
                firstCardLoading={isDataLoading && selectedVendorId === null}
                uom={firstCardUom}
                selectedVendorId={selectedVendorId}
                computedVendorTotals={computedVendorTotals}
                onSelectVendor={onSelectVendor}
              />

              <Box sx={isLoadFailed ? hiddenNoRowsOverlaySx : { display: 'contents' }}>
                <AgGridTable />
              </Box>
            </>
          </SlidingPanelContent>
        </AgGridClientTableProvider>
      </SelectionCountProvider>
    </Box>
  );
};
